import { Button, DataButton } from "@sellar-io/components";
import { OrderListResponseType } from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { v4 } from "uuid";
import { Icon, SpinnerLegacy } from "../../../../components";
import { useDashboardContext } from "../../../../components/dashboard/Dashboard.shared";
import { Description } from "../../../../components/description/Description";
import { ErrorMessage } from "../../../../components/errorMessage/ErrorMessage";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { PageContainer } from "../../../../components/pageContainer/PageContainer";
import { SUPPLIERS } from "../../../../constants/routes";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useOrderItemExport } from "../../../../hooks/api/v2/orderItem/useOrderItemExport";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { downloadCSV } from "../../../../utils/downloadHelpers";
import OrderListElement from "../orderListElement/OrderListElement";
import {
  OrderContainsValues,
  OrderFulfillmentFilterValues,
  OrderListSortValues,
  OrderPaymentStatusValues,
  OrderPaymentTypeValues,
  OrderStatusesFilterValues,
} from "./AllOrdersListFilterTypes";
import { SlideTransition } from "../../../../components/transition/SlideTransition";
import { AllOrdersListFilter } from "./AllOrdersListFilter";
import { useAllOrdersListStore } from "./AllOrdersListStore";
import { useShallow } from "zustand/react/shallow";
import { Tag } from "../../../../components/tag/Tag";
import { useMediaQuery } from "usehooks-ts";
import { FilterPopover } from "../../../../components/filterPopover/FilterPopover";
import { getOrderDetailsRedirectUrl } from "./AllOrdersList.shared";

export const AllOrdersList = ({
  query,
  selectedOrderId,
}: {
  query: UseInfiniteQueryResult<InfiniteData<OrderListResponseType>>;
  selectedOrderId: number | null;
}) => {
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();
  const { business } = useSellarCurrentBusiness();
  const [searchParams] = useSearchParams();
  const isAllOrdersTab =
    searchParams.get("tab") === "All orders" || Boolean(business?.Retailer);

  const route = getOrderDetailsRedirectUrl({
    orderId: selectedOrderId,
    isTwoPanel,
    query,
  });

  useEffect(() => {
    if (route && isAllOrdersTab) {
      //if two panel layout and no orders selected OR if the tab has changed, automatically select first order
      navigate(route, {
        replace: true,
      });
    }
  }, [route, isAllOrdersTab, navigate]);

  return (
    <>
      {query.isPending ? (
        <SpinnerLegacy />
      ) : (
        <>
          {query.isError ? (
            <ErrorMessage>
              {apiV2AssertError(query.error)?.message}
            </ErrorMessage>
          ) : (
            <>
              {!query.data.pages[0]?.length ? (
                <OrderListEmptyState />
              ) : (
                <InfiniteList query={query} className={"mw-[900px]"}>
                  {query.data.pages.map((page) =>
                    page.map((order, i) => (
                      <OrderListElement
                        key={i}
                        order={order}
                        selectedOrderId={selectedOrderId}
                      />
                    )),
                  )}
                </InfiniteList>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

const OrderListEmptyState = () => {
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  if (business?.Retailer) {
    return (
      <PageContainer padding={true}>
        <div className={"flex flex-col items-center gap-y-2 text-center"}>
          <div>
            <b>Get connected with suppliers on Sellar to place an order.</b>
          </div>
          <Description>
            View your order history and mange active orders here.
          </Description>
          <Button onClick={() => navigate(SUPPLIERS)}>
            Search for a supplier
          </Button>
        </div>
      </PageContainer>
    );
  }
  return (
    <div
      className={
        "flex flex-col items-center gap-y-2 pt-2 text-center font-bold"
      }
    >
      No orders
    </div>
  );
};

export const OrdersListMenu = () => {
  const { business } = useSellarCurrentBusiness();
  const state = useAllOrdersListStore(useShallow((state) => state));

  const [mobileFilterActive, setMobileFilterActive] = useState(false);

  const [desktopFilterActive, setDesktopFilterActive] = useState(false);
  const [clickOutside, setClickOutside] = useState(true);

  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const generateSortTag = (tag: string) => {
    if (tag.includes("shipped")) {
      return `Ship (${tag.split("(")[1]}`;
    }
    return tag;
  };

  return (
    <>
      <div className="max-w-screen bg-white md:max-w-none">
        <div className={twMerge("flex w-full items-center p-4 pb-2")}>
          <InputContainer className="relative mr-2 w-full py-[0.4rem]">
            <InputText
              required={false}
              placeholder={`Search ${business?.Supplier ? "customer" : "supplier"}, product, order ID or invoice...`}
              value={state.search}
              onChange={(e) => {
                state.setSearch(e.target.value);
              }}
              className="w-full"
            />
            {!!state.search && (
              <Button
                size="small"
                color="transparent"
                className="absolute right-0"
                onClick={() => state.setSearch("")}
              >
                <Icon icon="CLOSE" />
              </Button>
            )}
          </InputContainer>

          {!isDesktop && (
            <DataButton
              onClick={() => setMobileFilterActive(!mobileFilterActive)}
              border={true}
              active={mobileFilterActive}
              className="min-h-[38.78px]"
            >
              <Icon icon="FILTER_VARIANT" />
            </DataButton>
          )}
          {isDesktop && (
            <FilterPopover
              icon="FILTER_VARIANT"
              buttonText="Filter"
              compact={true}
              active={desktopFilterActive}
              toggleActive={setDesktopFilterActive}
              clickOutside={clickOutside}
            >
              <AllOrdersListFilter
                onDismiss={() => setDesktopFilterActive(false)}
                setClickOutside={(value?: boolean) =>
                  setClickOutside(value ?? !clickOutside)
                }
              />
            </FilterPopover>
          )}
        </div>

        <div className="flex overflow-x-auto">
          <div className="flex w-full items-center pb-4 pl-4">
            {state.sort !== "created_at_desc" && (
              <Tag
                className="mr-2"
                onClick={() => state.setSort("created_at_desc")}
                icon="SORT"
              >
                {generateSortTag(OrderListSortValues[state.sort].name)}
              </Tag>
            )}
            {state.orderStatuses.length > 0 &&
              state.orderStatuses.map((status, i) => (
                <Tag
                  key={i}
                  className="mr-2"
                  onClick={() =>
                    state.setOrderStatuses(
                      state.orderStatuses.filter(
                        (orderStatus) => orderStatus !== status,
                      ),
                    )
                  }
                  icon={OrderStatusesFilterValues[status].icon}
                >
                  {OrderStatusesFilterValues[status].name}
                </Tag>
              ))}
            {state.orderContains && (
              <Tag
                className="mr-2"
                onClick={() => state.setOrderContains("")}
                icon={
                  state.orderContains === "preorder"
                    ? "PROGRESS_CLOCK"
                    : undefined
                }
              >
                {OrderContainsValues[state.orderContains].name}
              </Tag>
            )}
            {state.deliveryStatus !== "" && (
              <Tag
                className="mr-2"
                onClick={() => state.setDeliveryStatus("")}
                icon="TRUCK_OUTLINE"
              >
                {OrderFulfillmentFilterValues[state.deliveryStatus].name}
              </Tag>
            )}
            {state.paymentStatus !== "" && (
              <Tag
                className="mr-2"
                onClick={() => state.setPaymentStatus("")}
                icon="FILE_DOCUMENT_CHECK_OUTLINE"
              >
                {OrderPaymentStatusValues[state.paymentStatus].name}
              </Tag>
            )}
            {state.paymentType !== "" && (
              <Tag
                className="mr-2"
                onClick={() => state.setPaymentType("")}
                icon="CREDIT_CARD"
              >
                {OrderPaymentTypeValues[state.paymentType].name}
              </Tag>
            )}
          </div>
        </div>

        {state.sort === "created_at_desc" && (
          <div className="px-4 pb-4 text-xs text-zinc-500">
            Sort by: {OrderListSortValues["created_at_desc"].name}
          </div>
        )}

        <SlideTransition
          show={mobileFilterActive}
          className={"w-full"}
          parentClass="w-full bg-white"
        >
          <AllOrdersListFilter onDismiss={() => setMobileFilterActive(false)} />
        </SlideTransition>
      </div>
    </>
  );
};

export const OrderItemExportButton = ({
  onClick,
}: {
  onClick?: () => void;
}) => {
  const [dialogActive, setDialogActive] = useState(false);

  const [date, setDate] = useState<string>(
    // Add 1 day as start of month defaults to midnight end of last month, so in the backend it sees this as the previous month. Adding one day ensures it picks the correct month
    dayjs().startOf("month").add(1, "day").toISOString(),
  );

  const orderItemExportQuery = useOrderItemExport(
    {
      month: dayjs(date).toDate(),
    },
    {
      enabled: false,
    },
  );

  const now = dayjs().startOf("month");

  const exportOptions = [...Array(12).keys()].map((i) =>
    // Add 1 day as start of month defaults to midnight end of last month, so in the backend it sees this as the previous month. Adding one day ensures it picks the correct month
    dayjs(now).subtract(i, "months").add(1, "day"),
  );

  return (
    <>
      <Button
        size={"large"}
        color={"secondary"}
        className={"w-full"}
        onClick={() => {
          if (onClick) onClick();
          setDialogActive(true);
        }}
      >
        <Icon icon={"DOWNLOAD_ARROW"} />
        Export
      </Button>
      <Dialog
        active={dialogActive}
        onDismiss={() => {
          if (onClick) onClick();
          setDialogActive(false);
        }}
      >
        <DialogNavigation
          title={"Export your order history"}
          description={
            "Export a CSV file of order items sold. You can download up to one month's worth of data at a time."
          }
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={() => {
            if (onClick) onClick();
            setDialogActive(false);
          }}
        />
        <DialogContent>
          <InputContainer>
            <InputSelect
              value={date}
              onChange={(e) => {
                console.log({ value: e.target.value });
                setDate(e.target.value);
              }}
            >
              {exportOptions.map((date) => (
                <InputOption key={v4()} value={date.toISOString()}>
                  {date.format("MMMM YYYY")}
                </InputOption>
              ))}
            </InputSelect>
          </InputContainer>
        </DialogContent>
        <DialogCTA>
          <Button
            disabled={!date}
            loading={orderItemExportQuery.isFetching}
            onClick={async () => {
              let currentCsv = orderItemExportQuery.data;

              if (!currentCsv || orderItemExportQuery.isStale) {
                const { data: newCsv } = await orderItemExportQuery.refetch();
                currentCsv = newCsv;
              }

              if (currentCsv) {
                downloadCSV(
                  [
                    "sellar-order-item-export-",
                    dayjs(date).format("YYYY-MM"),
                    ".csv",
                  ].join(""),
                  currentCsv,
                );
              }
            }}
          >
            Download CSV
          </Button>
        </DialogCTA>
      </Dialog>
    </>
  );
};
